import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ThemeButton } from '@shared/ui/buttons';
import Note from '@shared/ui/messages/Note';
import { useForm, useWatch } from 'react-hook-form';
import {
  BasicInput,
  BasicSelectInput,
  TelegramInput,
  MarketingPlatformsSelect,
} from '@shared/ui/forms/inputs';
import TabsComponent from '@shared/ui/TabsComponent';
import { FormGroup } from '@shared/ui/forms/display';
import { useSelector } from 'react-redux';
import { useProfile } from '@app/api/profile/useProfile';
import { useTranslation } from 'react-i18next';
import { validateMaxLength, validateServerErrors } from '@utils/validation';

const MONTHLY_VOLUME_OPTIONS = [
  { label: '<1K', value: '<1K' },
  { label: '1K-10K', value: '1K-10K' },
  { label: '10K-100K', value: '10K-100K' },
  { label: '10K-300K', value: '10K-300K' },
  { label: '>300K', value: '>300K' },
];

export default function StartVerificationDialog({ close }) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { onboard } = useProfile();

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: user.name,
      monthly_volume: '<1K',
      telegram: user.telegram || '',
      marketing_platforms: [],
      is_company: false,
      company_name: '',
    },
  });

  const isCompanyWatch = useWatch({ control, name: 'is_company' });

  async function onSubmit(formData) {
    const payload = { ...formData, is_company: formData.is_company ? 1 : 0 };
    delete payload.name;
    if (!formData.is_company) delete payload.company_name;
    try {
      const res = await onboard(payload);
      if (res) close();
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <>
      <DialogContent>
        <Note
          html
          type="info"
          marginBottom
          title={t('why_verification_is_required')}
          content={t('why_verification_is_required_content')}
        />
        <div className="form">
          <div className="formGroup-grid">
            <BasicInput
              disabled
              control={control}
              name="name"
              label={t('name')}
            />
            <BasicSelectInput
              control={control}
              name="monthly_volume"
              label={t('monthly_volume')}
              cleanable={false}
              isRequired
              data={MONTHLY_VOLUME_OPTIONS}
              valueKey="value"
              labelKey="label"
            />
          </div>
          <div className="formGroup-grid">
            <TelegramInput isRequired errors={errors} control={control} />
            <FormGroup label={t('are_you_representing_a_company')}>
              <TabsComponent
                fullWidth
                value={isCompanyWatch}
                setValue={(value) => setValue('is_company', value)}
                options={[
                  { label: t('no'), value: false, icon: 'close' },
                  { label: t('yes'), value: true, icon: 'check' },
                ]}
              />
            </FormGroup>
          </div>
          {isCompanyWatch && (
            <BasicInput
              error={errors.company_name}
              control={control}
              name="company_name"
              placeholder={t('company')}
              label={t('company')}
              isRequired={false}
              counter={true}
              validation={validateMaxLength}
            />
          )}
          <MarketingPlatformsSelect
            control={control}
            name="marketing_platforms"
            isRequired
            error={errors.marketing_platforms}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          minWidth
          startIcon="check"
          block={false}
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('submit')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
